import { formatPostcode } from './selection';
import { recommendedDriverFee, type DriverAreaQuote } from './driverRequestRecommendation';
import type { CashOrderDraft } from '../cash-handling/shared';

export interface RunDropDraft {
  key: string; postcode: string; lat: number | null; lng: number | null; areaLabel: string;
  addressText: string; phone: string; feeText: string; feePence: number; note: string; cash: CashOrderDraft;
}
export function runDropFeePence(text: string): number {
  if (!/^\d{1,5}(?:\.\d{0,2})?$/.test(text)) return 0;
  const [whole, fraction = ''] = text.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
}
export const validDropPoint = (lat: unknown, lng: unknown): boolean => typeof lat === 'number' && Number.isFinite(lat) && Math.abs(lat) <= 90 && typeof lng === 'number' && Number.isFinite(lng) && Math.abs(lng) <= 180;
export type RunDropLocation = Pick<RunDropDraft, 'postcode' | 'lat' | 'lng' | 'areaLabel'>;
export async function suggestRunDrop(input: Pick<RunDropDraft, 'postcode' | 'lat' | 'lng'>, storeId: number, headers: Record<string, string>, base: string, signal: AbortSignal, mapboxToken?:string): Promise<{location: RunDropLocation; areaFeePence: number | null; recommendedPence: number | null; warning: string}> {
  const controller = new AbortController(), abort = () => controller.abort();
  signal.addEventListener('abort', abort, {once:true}); if (signal.aborted) abort();
  const timeout = setTimeout(abort, 8000);
  let postcode = formatPostcode(input.postcode) ?? '', lat = input.lat, lng = input.lng, areaLabel = '';
  let areaFeePence: number | null = null, locationWarning = '';
  const point = validDropPoint(lat, lng);
  try {
    if (!postcode && point) {
      try {
        const r = await fetch(`https://api.postcodes.io/postcodes?lat=${lat}&lon=${lng}&radius=1000&limit=1`, {signal:controller.signal});
        const d = r.ok ? await r.json() : null, nearest = d?.result?.[0];
        postcode = formatPostcode(nearest?.postcode) ?? '';
      } catch { /* The selected map coordinates remain usable without a postcode. */ }
    }
    if (postcode) {
      const work: Promise<void>[] = [];
      if (!point) work.push((async () => {
        try {
          const r = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`, {signal:controller.signal}), d = r.ok ? await r.json() : null;
          if (validDropPoint(d?.result?.latitude, d?.result?.longitude)) { lat = d.result.latitude; lng = d.result.longitude; areaLabel = ''; }
          else locationWarning = 'Check the postcode and add the street address; a map location could not be confirmed.';
        } catch { locationWarning = 'Map lookup is unavailable. Check the postcode and add the street address.'; }
      })());
      work.push((async () => {
        try {
          const r = await fetch(`${base}/api/order/stores/${storeId}/external-deliveries/quote`, {method:'POST', headers, signal:controller.signal, body:JSON.stringify({destinationPostcode:postcode})});
          if (r.ok) { const q = await r.json() as DriverAreaQuote; if (Number.isSafeInteger(q.deliveryFeePence)) areaFeePence = q.deliveryFeePence; }
        } catch { /* Existing manual fee booking remains available. */ }
      })());
      await Promise.all(work);
    }
    if(mapboxToken&&validDropPoint(lat,lng)){
      try{
        const params=new URLSearchParams({longitude:String(lng),latitude:String(lat),types:'address,street,locality,neighborhood,place',country:'gb',language:'en',permanent:'true',access_token:mapboxToken});
        const r=await fetch(`https://api.mapbox.com/search/geocode/v6/reverse?${params}`,{signal:controller.signal});
        if(r.ok){const data=await r.json();areaLabel=runDropPlaceLabel(data,point);}
      }catch{/* Keep exact coordinates and an honest postcode/pin fallback. */}
      if(!areaLabel)locationWarning=[locationWarning,'Street/place lookup unavailable. Check the pin and enter the delivery address.'].filter(Boolean).join(' ');
    }
    const recommendedPence = areaFeePence === null ? null : recommendedDriverFee(areaFeePence);
    return { location:{postcode,lat:validDropPoint(lat,lng)?lat:null,lng:validDropPoint(lat,lng)?lng:null,areaLabel:areaLabel || postcode || 'Selected map point'}, areaFeePence, recommendedPence, warning:[locationWarning,recommendedPence===null?'No suggested fee is available. Set a delivery fee of at least £3.':''].filter(Boolean).join(' ') };
  } finally { clearTimeout(timeout); signal.removeEventListener('abort',abort); }
}

export function runDropPlaceLabel(data:any,includeStreet=true):string {
 const features=Array.isArray(data?.features)?data.features:[];
 const candidate=features.find((f:any)=>f?.properties?.feature_type==='address'||f?.properties?.feature_type==='street')??features.find((f:any)=>['locality','neighborhood','place'].includes(f?.properties?.feature_type));
 const p=candidate?.properties;if(!p)return '';
 const c=p.context??{};
 const town=c.locality?.name??c.neighborhood?.name??(['locality','neighborhood'].includes(p.feature_type)?p.name:null)??c.place?.name??(p.feature_type==='place'?p.name:'');
 const street=c.street?.name??c.address?.street_name??(p.feature_type==='street'?p.name:'');
 return includeStreet&&street?`Near ${[street,town].filter(Boolean).join(', ')}`:town||'';
}
